import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { Clickable } from '@carvana/merch-components';
import { DispatcherEvent } from '@carvana/merch-native';
import { useCallback, useContext, useEffect, useState } from 'react';
import Carousel from '@/common/Carousel/Carousel';
import { useGetConsumerHref } from '@/common/SearchBar/utils';
import TrackWrapper from '~/components/common/TrackWrapper';
import SortOption from '~/constants/common/SortOption';
import { useGetUser } from '~/context/common/UserContext';
import ModalContext from '~/context/home/ModalContext';
import SavedSearchesContext from '~/context/home/SavedSearchesContext';
import type { RecentSearch } from '~/contracts/home';
import useCarouselSettings from '~/hooks/home/useCarouselSettings';
import useHeaderAccountModal from '~/hooks/home/useHeaderAccountModal';
import HeartWithBorder from '~/icons/HeartWithBorder';
import { nativeSrpPayload } from '~/native/home/utils';
import { deleteSavedSearch } from '~/services/home';
import getMonthAndDayWithOrdinalSuffix from '~/utilities/date/getMonthAndDayWithOrdinalSuffix';
import decodeBase64 from '~/utilities/decodeBase64';
import encodeBase64 from '~/utilities/encodeBase64';
import {
  TileWrapper,
  TileTopSectionWrapper,
  TileTopSectionContent,
  Divider,
  Pill
} from './RecentSearches.styles';

type RecentSearchTileProps = {
  tileInfo: RecentSearch;
};

const addNewestInventorySortToCvnaId = (cvnaId: string): string => {
  const { filters } = JSON.parse(decodeBase64(cvnaId));

  return encodeBase64(
    JSON.stringify({
      filters,
      sortBy: SortOption.NewestInventory
    })
  );
};

function RecentSearchTile({ tileInfo }: RecentSearchTileProps) {
  const {
    cvnaId,
    savedSearch: { savedSearchId = 0, name = '' } = {},
    display: { name: displayName, additionalFiltersCount },
    totalMatchingVehicles,
    totalNewVehicles,
    searchExecutedDateTime
  } = tileInfo;
  const { email = '' } = useGetUser();
  const getHref = useGetConsumerHref();
  const { setSavedSearches } = useContext(SavedSearchesContext);
  const { setCreateSavedSearchOpen } = useContext(ModalContext);
  const { trackEvent } = useContext(AnalyticsContext);
  const [currentSavedSearchId, setCurrentSavedSearchId] =
    useState(savedSearchId);
  const [alertName, setAlertName] = useState(name || displayName);

  useEffect(() => {
    setCurrentSavedSearchId(savedSearchId);
    setAlertName(name || displayName);
  }, [savedSearchId, name, displayName]);

  const successHandler = useCallback(() => {
    setCreateSavedSearchOpen(true, {
      alertName,
      cvnaid: cvnaId
    });
  }, [setCreateSavedSearchOpen, alertName, cvnaId]);

  const openModal = useHeaderAccountModal({
    callerIdentifier: 'SearchAlert',
    headerModalTitle: 'SAVE YOUR SEARCH',
    successHandler,
    openModal: successHandler,
    nativeOptions: {
      eventDispatch: DispatcherEvent.AddSavedSearch,
      eventPayload: {
        alertName,
        cvnaid: cvnaId
      },
      eventSource: 'home'
    }
  });

  const toggleSavedSearch = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (Boolean(currentSavedSearchId)) {
        deleteSavedSearch({
          email,
          savedSearchId: currentSavedSearchId
        }).then(res => {
          setSavedSearches(res.data.savedSearches ?? []);
          setCurrentSavedSearchId(0);
          setAlertName(displayName);
        });

        trackEvent('Clicked - Home feed card', {
          description: 'Recent search heart cta',
          status: 'removed',
          content: tileInfo.display
        });

        return;
      }
      openModal();

      trackEvent('Clicked - Home feed card', {
        description: 'Recent search heart cta',
        status: 'opened',
        content: tileInfo.display
      });
    },
    [
      currentSavedSearchId,
      displayName,
      email,
      openModal,
      setSavedSearches,
      tileInfo.display,
      trackEvent
    ]
  );

  return (
    <a
      href={getHref(
        `/cars/filters?cvnaid=${addNewestInventorySortToCvnaId(cvnaId)}`
      )}
      data-analytics-id="Home feed card"
      data-analytics-attributes={JSON.stringify({
        description: 'Recent search tile',
        content: tileInfo.display
      })}
      data-native-payload={nativeSrpPayload}
    >
      <TileWrapper>
        <TileTopSectionWrapper>
          <TileTopSectionContent>
            <div className="tl-body-xs">
              Last Searched{' '}
              {getMonthAndDayWithOrdinalSuffix(searchExecutedDateTime)}
            </div>
            <div
              className="text-blue-6 tl-header-s"
              data-qa="recent-searches-alert-name"
            >
              {alertName}
            </div>
            {additionalFiltersCount ? (
              <div className="tl-body-s">
                +{additionalFiltersCount} filter
                {additionalFiltersCount > 1 ? 's' : ''}
              </div>
            ) : null}
          </TileTopSectionContent>
          <Clickable
            onClick={toggleSavedSearch}
            data-analytics-id="Home feed card"
            data-qa="saved search"
            className="pointer-events-auto"
          >
            <HeartWithBorder
              lightBlue
              isFavorited={Boolean(currentSavedSearchId)}
              className="h-20 w-20"
            />
          </Clickable>
        </TileTopSectionWrapper>
        <Divider />
        <div className="flex items-start gap-4">
          {totalMatchingVehicles ? (
            <Pill
              className="border border-blue-2 text-blue-2"
              data-qa="matched-results"
            >
              {totalMatchingVehicles} Results
            </Pill>
          ) : null}
          {totalNewVehicles ? (
            <Pill className="border bg-marketplace-purple text-white">
              {totalNewVehicles} New
            </Pill>
          ) : null}
        </div>
      </TileWrapper>
    </a>
  );
}

type OwnProps = {
  recentSearches: RecentSearch[];
};

export default function RecentSearches({ recentSearches }: OwnProps) {
  const { values, carouselRef } = useCarouselSettings({
    className: 'recent-searches-slider',
    heading: 'Recent searches',
    maxLength: recentSearches?.length
  });

  const viewEvent = {
    eventName: 'Viewed - Home feed section',
    customAttributes: {
      description: 'Recent Searches',
      isCarousel: true,
      content: {
        searchTerms: recentSearches
      }
    }
  };

  return (
    <>
      {recentSearches.length > 0 ? (
        <TrackWrapper event={viewEvent}>
          <Carousel {...values} ref={carouselRef} dataQa="recent-searches">
            {recentSearches.map((recentSearch: RecentSearch) => {
              return (
                <RecentSearchTile
                  key={recentSearch.display.name}
                  tileInfo={recentSearch}
                />
              );
            })}
          </Carousel>
        </TrackWrapper>
      ) : null}
    </>
  );
}
